import { debt_register_api } from '@axo/shared/data-access/api';
import { DataAccessContext, useAPI } from '@axo/shared/data-access/provider';
import type {
  DebtRegistryConsent,
  DebtRegistryPatchConsentData,
} from '@axo/shared/data-access/types/debt_registry';
import { useContext } from 'react';
import { type QueryFunctionContext, useQuery } from 'react-query';
import {
  debtRegistryKeys,
  parseDebtRegistryKeys,
} from '../../cache-keys/debtRegistryKeys.tsx';
import { queryFunction } from '../../query-function.ts';

function map({
  queryKey,
}: QueryFunctionContext<
  ReturnType<(typeof debtRegistryKeys)['cachedConsent']>
>) {
  const { applicationID, personID } = parseDebtRegistryKeys(queryKey);
  return { applicationID: applicationID ?? '', personID: personID ?? '' };
}

interface UseCachedDebtRegistryConsent {
  applicationID: string;
  personID: string;
}

interface QueryOptions {
  enabled?: boolean;
  onSuccess?: (data: DebtRegistryConsent) => void;
  onError?: (error?: unknown) => void;
  onSettled?: (data?: DebtRegistryConsent, error?: unknown) => void;
}

export function useCachedDebtRegistryConsent(
  { applicationID, personID }: UseCachedDebtRegistryConsent,
  options?: QueryOptions,
) {
  const { state } = useContext(DataAccessContext);
  const api = useAPI();
  const queryKey = debtRegistryKeys.cachedConsent({
    applicationID,
    personID,
  });

  const { enabled = true } = options || {};

  return useQuery({
    queryKey,
    queryFn: queryFunction(debt_register_api.getConsent(api), map),
    enabled:
      state.user.token.length > 0 &&
      Boolean(applicationID) &&
      Boolean(personID) &&
      enabled,
    retry: 1,
    ...options,
  });
}
